/* global process */
// @ts-check

import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import HttpClient from '../../../services/HttpClient'
import CharacterCard from '../components/CharacterCard'
import '../components/Characters.styles.css'
import './CharactersDirectory.css'

/**
 * @typedef {Object} DirectoryCharacter
 * @property {string|number} characterId
 * @property {string} slug
 * @property {string} name
 * @property {boolean=} academyMode
 */

/**
 * Directorio público de shinobis (fichas aprobadas). Prep Conociendo personajes.
 */
export default function CharactersDirectoryPage({ funcNav = () => {}, funcCharNavbar = () => {} }) {
  const navigate = useNavigate()
  const [characters, setCharacters] = useState(/** @type {DirectoryCharacter[]} */ ([]))
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(/** @type {string|null} */ (null))
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (typeof funcNav === 'function') funcNav(true)
    if (typeof funcCharNavbar === 'function') funcCharNavbar(false)
  }, [funcNav, funcCharNavbar])

  const loadDirectory = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const { data } = await HttpClient().get('/api/v1/characters/directory')
      setCharacters(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err?.message || 'Error al cargar el directorio')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadDirectory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return characters
    return characters.filter((c) => String(c?.name || '').toLowerCase().includes(q))
  }, [characters, search])

  const handleCardClick = (slug) => navigate(`/personajes/${slug}`)

  return (
    <div className="page charactersPageShell charactersDirectoryShell">
      <div className="charactersPage charactersDirectory">
        <header className="characters-directory-hero">
          <div className="characters-directory-hero__overlay" aria-hidden="true" />
          <div className="characters-directory-hero__content">
            <div className="characters-directory-hero__main">
              <div className="characters-directory-hero__emblem" aria-hidden="true">
                忍
              </div>
              <div className="characters-directory-hero__text">
                <span className="characters-directory-hero__eyebrow">人物 · Mundo</span>
                <h1 className="characters-directory-hero__title">Shinobis</h1>
                <p className="characters-directory-hero__subtitle">
                  Nombres que ya caminan el continente. Aquí no hay verdades selladas — solo huellas.
                  El cuaderno de observación llegará para anotarlas.
                </p>
              </div>
            </div>
            <Link to="/mis-personajes" className="characters-directory-hero__cta">
              Mis shinobis →
            </Link>
          </div>
          <div className="characters-directory-hero__bottom-line" aria-hidden="true" />
        </header>

        <div className="charactersToolbar charactersDirectory__toolbar">
          <label className="charactersToolbar__search">
            <span className="visually-hidden">Buscar por nombre</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre…"
              autoComplete="off"
            />
          </label>
          <p className="charactersDirectory__count" aria-live="polite">
            {isLoading ? '…' : `${filtered.length} personaje${filtered.length === 1 ? '' : 's'}`}
          </p>
        </div>

        {isLoading ? (
          <div className="charactersGrid" aria-label="Cargando directorio">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="skeletonCard" />
            ))}
          </div>
        ) : null}

        {!isLoading && error ? (
          <div className="charactersAlert" role="alert">
            <p>{error}</p>
            <button type="button" className="mainHero__cta" onClick={loadDirectory}>
              Reintentar
            </button>
          </div>
        ) : null}

        {!isLoading && !error && filtered.length === 0 ? (
          <div className="charactersDirectory__empty" role="status">
            <p>No hay personajes aprobados que coincidan.</p>
          </div>
        ) : null}

        {!isLoading && !error && filtered.length > 0 ? (
          <div className="charactersGrid" role="list">
            {filtered.map((c) => (
              <CharacterCard
                key={c.characterId}
                id={c.characterId}
                slug={c.slug}
                name={c.name}
                academyMode={!!c.academyMode}
                imageUrl={`/assets/images/${c.slug}.png`}
                publicMode
                onClick={handleCardClick}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

CharactersDirectoryPage.propTypes = {
  funcNav: PropTypes.func,
  funcCharNavbar: PropTypes.func,
}
